import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useState } from "react";
import { defineMessages, useIntl } from "react-intl";
import { usePageState } from "../../PageContext";
import { usePages } from "../../pagesContext";
import { visuallyHiddenStyle } from "../../hooks/visuallyHiddenStyle";
/**
 * Delay (ms) before filling the live region on first render so the
 * empty -> filled transition is observable to the screen reader.
 * See block comment inside StepperAnnouncement for rationale.
 * Tested with NVDA; not yet validated on JAWS / VoiceOver.
 */
const INITIAL_ANNOUNCEMENT_DELAY_MS = 150;
export const stepperMessages = defineMessages({
    stepAnnouncement: {
        id: "template-form.stepper.step-announcement",
        defaultMessage: "Step {current} of {total}: {title}"
    },
    formStepsNav: {
        id: "template-form.stepper.nav-label",
        defaultMessage: "Form steps"
    }
});
/**
 * Visually hidden live region that announces the current step to screen readers.
 * Renders empty on mount and fills after a short delay so that the initial step
 * is also announced (aria-live only triggers on content changes, not initial render).
 */
export function StepperAnnouncement() {
    const intl = useIntl();
    const pages = usePages();
    const { step } = usePageState();
    // Render empty on mount, then fill after a short delay so the first step
    // is announced too: aria-live only speaks content *changes*, not initial
    // content, so we need an observable transition from empty -> filled.
    //
    // The delay must be long enough for the screen reader to have registered
    // the live region before we mutate it. Empirically (NVDA only, not yet
    // validated on JAWS / VoiceOver):
    //   - requestAnimationFrame (~16ms): too fast on full page loads; NVDA
    //     often missed the announcement.
    //   - 50-100ms: still intermittent on slower machines.
    //   - 150ms: reliable, imperceptible to sighted users.
    // Don't reduce without re-testing on a full-reload scenario.
    const [ready, setReady] = useState(false);
    useEffect(() => {
        const id = setTimeout(() => setReady(true), INITIAL_ANNOUNCEMENT_DELAY_MS);
        return () => clearTimeout(id);
    }, []);
    const displayPages = pages.filter(p => p.displayInStepper);
    let displayStep = 0;
    for (let i = 0; i <= step && i < pages.length; i++) {
        if (pages[i].displayInStepper) {
            displayStep++;
        }
    }
    if (displayStep === 0 || displayPages.length === 0)
        return null;
    return (_jsx("div", { role: "status", "aria-live": "polite", "aria-atomic": "true", style: visuallyHiddenStyle, children: ready &&
            intl.formatMessage(stepperMessages.stepAnnouncement, {
                current: displayStep,
                total: displayPages.length,
                title: pages[step]?.pageTitle ?? ""
            }) }));
}
//# sourceMappingURL=StepperAnnouncement.js.map